// mapbox
import mapboxgl from 'mapbox-gl';
import * as turf from '@turf/turf';

// config
import geoRecordLayers from '@/vue3/configs/geoRecordLayers';
import modificationLayers from '@/vue3/configs/mapbox/geoRecordModificationLayers';

// hooks
import { useVertexHook } from '@/vue3/hooks/geoRecordVertexLabelHook';

// hooks
const { toggleVertexLabelVisibility, removeVertexLabel, updateVertexLabel } =
    useVertexHook();

// store
import { useStore } from '@/store';

export const useGeoRecord = () => {
    // store
    const store = useStore();
    const isMeters = store.state.isMeters;

    const toggleGeoRecord = (
        map: mapboxgl.Map,
        geoRecord: any,
        isVisible: boolean,
        tree: any,
        layerTreeState: any,
    ) => {
        const parentNode = findNode(tree[0], geoRecord.id);
        const childNodes = getChildNodes(parentNode.children, []);

        const ogVisibility = isVisible;
        const source = map.getSource('geo-records');
        const extrusionSource = map.getSource('geo-records-extrusion');
        const extrusionFeatures = extrusionSource._data.features;
        const features = source._data.features;

        if (parentNode.type !== 'geo_record') {
            childNodes.forEach((node: any) => {
                isVisible = ogVisibility;

                layerTreeState[node.id].isVisible = isVisible;
                features.find(
                    (target: any) => target.id == node.id,
                ).properties.isVisible = isVisible;
                toggleVertexLabelVisibility(
                    map,
                    features.find((target: any) => target.id == node.id),
                    isVisible,
                );
                let extrusion = extrusionFeatures.find(
                    (target: any) => target.id === node.id,
                );

                if (extrusion) {
                    extrusion.properties.isVisible = isVisible;
                }
            });
        } else {
            isVisible = ogVisibility;

            layerTreeState[geoRecord.id].isVisible = isVisible;
            features.find(
                (target: any) => target.id == parentNode.id,
            ).properties.isVisible = isVisible;
            toggleVertexLabelVisibility(
                map,
                features.find((target: any) => target.id == parentNode.id),
                isVisible,
            );
            let extrusion = extrusionFeatures.find(
                (target: any) => target.id === parentNode.id,
            );

            if (extrusion) {
                extrusion.properties.isVisible = isVisible;
            }
        }

        source.setData({
            type: 'FeatureCollection',
            features: features,
        });

        extrusionSource.setData({
            type: 'FeatureCollection',
            features: extrusionFeatures,
        });
    };

    // given a parent node return an array of all its child geo_record nodes
    const getChildNodes = (nodes: any, foundNodes: any[]) => {
        nodes.forEach((node: any) => {
            if (node.type == 'geo_record') {
                foundNodes.push(node);
            }
            if (node.children.length > 0) {
                getChildNodes(node.children, foundNodes);
            }
        });
        return foundNodes;
    };

    // given a parent node and target node id return the found node otherwise return null
    const findNode = (node: any, target: number) => {
        if (node.id == target) {
            return node;
        } else if (node.children.length > 0) {
            var result = null;
            for (var i = 0; result == null && i < node.children.length; i++) {
                result = findNode(node.children[i], target);
            }
            return result;
        }
        return null;
    };

    const generateMapboxFeature = (geoRecord: any) => {
        let labelState;
        if (
            geoRecord.labelState === 'auto' ||
            !geoRecord.labelState ||
            geoRecord.labelState === 'on'
        ) {
            labelState = 'on';
        } else {
            labelState = 'off';
        }
        let visibility =
            geoRecord.isVisible != undefined ? geoRecord.isVisible : true;
        return {
            id: geoRecord.id,
            type: 'Feature',
            geometry: geoRecord.geometry,
            properties: {
                opacity: geoRecord.opacity ?? 0.5,
                isFill: geoRecord.isFill,
                accountId: geoRecord.accountId,
                title: geoRecord.title,
                color: geoRecord.color
                    ? geoRecord.color
                    : store.state.isKeyLineView
                    ? '#333333'
                    : '#fff',
                isVisible: visibility,
                geoRecordCategoryId: geoRecord.geoRecordCategoryId,
                assigneeAccountId: geoRecord.assigneeAccountId,
                courseSchemaId: geoRecord.courseSchemaId,
                dueDate: geoRecord.dueDate,
                endDate: geoRecord.endDate,
                geoRecordCategoryOrder: geoRecord.geoRecordCategoryOrder,
                id: geoRecord.id,
                isTask: geoRecord.isTask,
                organizationId: geoRecord.organizationId,
                projectId: geoRecord.projectId,
                startDate: geoRecord.startDate,
                statusId: geoRecord.statusId,
                height: geoRecord.height ? geoRecord.height : 0,
                labelState: labelState,
                vertexLabelState: geoRecord.vertexLabelState,
                isRectangle: geoRecord.isRectangle,
                archiveEventId: geoRecord.archiveEventId,
                tagIds: geoRecord.tagIds,
            },
        };
    };

    const initGeoRecordSource = (
        map: mapboxgl.Map,
        geoRecords: any,
        layerTreeState: any,
    ) => {
        const geoRecordSource = {
            type: 'geojson',
            maxzoom: 16,
            buffer: 512,
            data: {
                type: 'FeatureCollection',
                features: geoRecords.map((x) => {
                    const geoRecord = generateMapboxFeature(x);
                    geoRecord.properties.isVisible =
                        layerTreeState[geoRecord.id].isVisible;
                    return geoRecord;
                }),
            },
        };
        if (map.getSource('geo-records')) {
            map.removeSource('geo-records');
        }
        map.addSource('geo-records', geoRecordSource);

        const geoRecordVertexLabelSource = {
            type: 'geojson',
            maxzoom: 16,
            buffer: 512,
            data: {
                type: 'FeatureCollection',
                features: [] as any,
            },
        };
        if (map.getSource('geo-records-vertex-label')) {
            map.removeSource('geo-records-vertex-label');
        }
        map.addSource('geo-records-vertex-label', geoRecordVertexLabelSource);
        geoRecords.forEach((geoRecord: any) => {
            updateVertexLabel(geoRecord, map, isMeters);
        });
    };

    const initArchiveGeoRecordSource = (
        map: mapboxgl.Map,
        archiveGeoRecords: any,
    ) => {
        const archiveGeoRecordSource = {
            type: 'geojson',
            maxzoom: 16,
            buffer: 512,
            data: {
                type: 'FeatureCollection',
                features: archiveGeoRecords.map((geoRecord) => {
                    const archiveGeoRecord = generateMapboxFeature(geoRecord);
                    archiveGeoRecord.properties.isVisible = false;
                    return archiveGeoRecord;
                }),
            },
        };

        if (map.getSource('geo-records-archive')) {
            map.removeSource('geo-records-archive');
        }
        map.addSource('geo-records-archive', archiveGeoRecordSource);

        const geoRecordVertexLabelSource = {
            type: 'geojson',
            maxzoom: 16,
            buffer: 512,
            data: {
                type: 'FeatureCollection',
                features: [] as any,
            },
        };

        if (map.getSource('geo-records-vertex-label-archive')) {
            map.removeSource('geo-records-vertex-label-archive');
        }
        map.addSource(
            'geo-records-vertex-label-archive',
            geoRecordVertexLabelSource,
        );

        archiveGeoRecords.forEach((archiveGeoRecord: any) => {
            updateVertexLabel(archiveGeoRecord, map, isMeters);
        });
    };

    const refreshGeoRecord = (
        map: mapboxgl.Map,
        geoRecord: any,
        isVisible: boolean,
    ) => {
        if (geoRecord.id == 0) return;

        if (isVisible === undefined) {
            isVisible = true;
        }

        if (!geoRecord.color || geoRecord.geoRecordCategoryId === 0) {
            geoRecord.color = '#fff';
        }

        const source = map.getSource('geo-records');
        const features = source._data.features;
        const featureIndex = features.findIndex((x) => x.id === geoRecord.id);
        features[featureIndex] = generateMapboxFeature(geoRecord);

        features[featureIndex].properties.isVisible = isVisible;

        source.setData({
            type: 'FeatureCollection',
            features: features,
        });

        if (geoRecord.geometry) {
            updateVertexLabel(geoRecord, map, isMeters);
        }
    };

    const removeGeoRecord = (map: mapboxgl.Map, geoRecord: any) => {
        const source = map.getSource('geo-records');
        let features = source._data.features;
        features = features.filter((x) => x.id !== geoRecord.id);
        source.setData({
            type: 'FeatureCollection',
            features: features,
        });
        removeVertexLabel(map, geoRecord);
    };

    const addGeoRecord = (map: mapboxgl.Map, geoRecord: any) => {
        const source = map.getSource('geo-records');
        const features = source._data.features;
        const newFeature = generateMapboxFeature(geoRecord);
        features.push(newFeature);
        refreshGeoRecord(map, geoRecord, true);
    };

    const modifyGeometry = (
        draw: mapboxgl.Draw,
        map: mapboxgl.Map,
        geoRecord: any,
    ) => {
        const id = draw.add({
            id: 'test',
            type: 'Feature',
            properties: {
                rotation: geoRecord.rotation ? geoRecord.rotation : 0,
                isRectangle: geoRecord.isRectangle,
                vertexLabelState: geoRecord.vertexLabelState,
                smoothCount: geoRecord.smoothCount,
            },
            geometry: geoRecord.geometry,
            height: geoRecord.height,
        });
        refreshGeoRecord(map, geoRecord, false);
    };

    const initGeoRecordExtrusion = (
        map: mapboxgl.Map,
        geoRecords: number[],
        visibleRecords: number[],
    ) => {
        const newGeoRecords = new Array();
        geoRecords.forEach((geoRecord: any) => {
            geoRecord.isVisible =
                visibleRecords &&
                visibleRecords.length > 0 &&
                visibleRecords.includes(geoRecord.id);
            if (geoRecord.height !== null && geoRecord.height > 0) {
                let selectedGeorecord = geoRecord;

                if (geoRecord.geometry.type === 'LineString') {
                    const lineString = turf.lineString(
                        selectedGeorecord.geometry.coordinates,
                    );
                    const buffered = turf.buffer(lineString, 0.0000009, {
                        units: 'degrees',
                    });

                    selectedGeorecord.geometry = buffered.geometry;
                } else if (geoRecord.geometry.type === 'Point') {
                    const point = turf.point(
                        selectedGeorecord.geometry.coordinates,
                    );
                    const buffered = turf.circle(point, 0.0000009, {
                        units: 'degrees',
                    });
                    selectedGeorecord.geometry = buffered.geometry;
                }
                newGeoRecords.push(selectedGeorecord);
            }
        });
        const geoRecordExtrusionSource = {
            type: 'geojson',
            maxzoom: 16,
            buffer: 512,
            data: {
                type: 'FeatureCollection',
                features: newGeoRecords.map((x) => {
                    return generateMapboxFeature(x);
                }),
            },
        };
        map.addSource('geo-records-extrusion', geoRecordExtrusionSource);
        map.addLayer(
            {
                id: 'polygon-extrusion',
                type: 'fill-extrusion',
                source: 'geo-records-extrusion',
                paint: {
                    'fill-extrusion-color': ['get', 'color'],
                    'fill-extrusion-height': ['get', 'height'],
                    'fill-extrusion-opacity': 0.6,
                },
                filter: [
                    'all',
                    ['==', ['geometry-type'], 'Polygon'],
                    ['==', ['get', 'isVisible'], true],
                ],
            },
            'geo-record-title',
        );
    };

    const initGeoRecordArchiveExtrusion = (
        map: mapboxgl.Map,
        geoRecords: any,
    ) => {
        const newGeoRecords = new Array();
        geoRecords.forEach((geoRecord: any) => {
            if (
                geoRecord.isVisible &&
                geoRecord.height !== null &&
                geoRecord.height > 0
            ) {
                geoRecord.isVisible = true;
                let selectedGeorecord = geoRecord;

                if (geoRecord.geometry.type === 'LineString') {
                    const lineString = turf.lineString(
                        selectedGeorecord.geometry.coordinates,
                    );
                    const buffered = turf.buffer(lineString, 0.0000009, {
                        units: 'degrees',
                    });

                    selectedGeorecord.geometry = buffered.geometry;
                } else if (geoRecord.geometry.type === 'Point') {
                    const point = turf.point(
                        selectedGeorecord.geometry.coordinates,
                    );
                    const buffered = turf.circle(point, 0.0000009, {
                        units: 'degrees',
                    });
                    selectedGeorecord.geometry = buffered.geometry;
                }
                newGeoRecords.push(selectedGeorecord);
            }
        });

        if (map.getLayer('polygon-archive-extrusion')) {
            map.removeLayer('polygon-archive-extrusion');
            map.removeSource('geo-records-archive-extrusion');
        }

        const geoRecordExtrusionSource = {
            type: 'geojson',
            maxzoom: 16,
            buffer: 512,
            data: {
                type: 'FeatureCollection',
                features: newGeoRecords.map((x) => {
                    return generateMapboxFeature(x);
                }),
            },
        };
        map.addSource(
            'geo-records-archive-extrusion',
            geoRecordExtrusionSource,
        );
        map.addLayer(
            {
                id: 'polygon-archive-extrusion',
                type: 'fill-extrusion',
                source: 'geo-records-archive-extrusion',
                paint: {
                    'fill-extrusion-color': ['get', 'color'],
                    'fill-extrusion-height': ['get', 'height'],
                    'fill-extrusion-opacity': 0.6,
                },
                filter: [
                    'all',
                    ['==', ['geometry-type'], 'Polygon'],
                    ['==', ['get', 'isVisible'], true],
                ],
            },
            'geo-record-title',
        );
    };
    const removeGeoRecordArchiveExtrusionLayers = (map: mapboxgl.Map) => {
        if (map.getLayer('polygon-archive-extrusion')) {
            map.removeLayer('polygon-archive-extrusion');
            map.removeSource('geo-records-archive-extrusion');
        }
    };

    const removeGeoRecordExtrusionLayers = (map: mapboxgl.Map) => {
        map.removeLayer('polygon-extrusion');
    };

    const addGeoRecordExtrusion = (map: mapboxgl.Map, geoRecord: any) => {
        const source = map.getSource('geo-records-extrusion');
        const features = source._data.features;
        const newFeature = generateMapboxFeature(geoRecord);
        features.push(newFeature);
        source.setData({
            type: 'FeatureCollection',
            features: features,
        });
    };

    const updateGeoRecordExtrusion = (map: mapboxgl.Map, geoRecord: any) => {
        if (map.getSource('geo-records-extrusion')) {
            const source = map.getSource('geo-records-extrusion');
            const features = source._data.features;
            const featureIndex = features.findIndex(
                (x) => x.id === geoRecord.id,
            );
            features[featureIndex] = generateMapboxFeature(geoRecord);
            source.setData({
                type: 'FeatureCollection',
                features: features,
            });
        }
    };

    const removeGeoRecordExtrusion = (map: mapboxgl.Map, geoRecord: any) => {
        const source = map.getSource('geo-records-extrusion');

        if (!source) return;

        let features = source._data.features;
        features = features.filter((x) => x.id !== geoRecord.id);
        source.setData({
            type: 'FeatureCollection',
            features: features,
        });
    };

    const refreshGeoRecordExtrusion = (map: mapboxgl.Map, geoRecord: any) => {
        let selectedGeorecord = geoRecord;
        if (geoRecord.geometry.type === 'LineString' && geoRecord.height > 0) {
            const lineString = turf.lineString(
                selectedGeorecord.geometry.coordinates,
            );
            const buffered = turf.buffer(lineString, 0.0000009, {
                units: 'degrees',
            });

            selectedGeorecord.geometry = buffered.geometry;
        } else if (
            geoRecord.geometry.type === 'Point' &&
            geoRecord.height > 0
        ) {
            const point = turf.point(selectedGeorecord.geometry.coordinates);
            const buffered = turf.circle(point, 0.0000009, {
                units: 'degrees',
            });
            selectedGeorecord.geometry = buffered.geometry;
        }

        const source = map.getSource('geo-records-extrusion');
        let features = source._data.features;
        if (!selectedGeorecord.color) {
            selectedGeorecord.color = '#fff';
        }
        if (
            selectedGeorecord.height === 0 ||
            selectedGeorecord.height === null
        ) {
            features = features.filter((x) => x.id !== selectedGeorecord.id);
        } else {
            const featureIndex = features.findIndex(
                (x) => x.id === selectedGeorecord.id,
            );
            if (featureIndex !== -1) {
                features[featureIndex] =
                    generateMapboxFeature(selectedGeorecord);
            } else {
                features.push(generateMapboxFeature(selectedGeorecord));
            }
        }

        source.setData({
            type: 'FeatureCollection',
            features: features,
        });
    };

    const createGeoRecordExtrusion = (map: mapboxgl.Map, geoRecord: any) => {
        const source = map.getSource('geo-records-extrusion');
        const features = source._data.features;

        const newFeature = {
            id: 'temp-extrusion',
            type: 'Feature',
            geometry: geoRecord.geometry,
            properties: {
                color: '#ff0',
                isVisible: true,
                height: 0,
            },
        };

        features.push(newFeature);
        source.setData({
            type: 'FeatureCollection',
            features: features,
        });
    };

    const refreshCreatedGeoRecordExtrusion = (
        map: mapboxgl.Map,
        geoRecord: any,
    ) => {
        const source = map.getSource('geo-records-extrusion');
        let features = source._data.features;

        const featureIndex = features.findIndex(
            (x) => x.id === 'temp-extrusion',
        );
        features[featureIndex] = {
            id: 'temp-extrusion',
            type: 'Feature',
            geometry: geoRecord.geometry,
            properties: {
                color: '#ff0',
                isVisible: true,
                height: geoRecord.height ? geoRecord.height : 0,
            },
        };
        source.setData({
            type: 'FeatureCollection',
            features: features,
        });
    };

    const removeCreatedGeoRecordExtrusion = (map: mapboxgl.Map) => {
        const source = map.getSource('geo-records-extrusion');

        if (!source) return;

        let features = source._data.features;
        features = features.filter((x) => x.id !== 'temp-extrusion');
        source.setData({
            type: 'FeatureCollection',
            features: features,
        });
    };

    const addHighlightGeoRecordExtrusion = (
        map: mapboxgl.Map,
        geoRecordExtrusion: any,
    ) => {
        const geoRecordExtrusionSource = {
            type: 'geojson',
            maxzoom: 16,
            buffer: 512,
            data: {
                type: 'FeatureCollection',
                features: [geoRecordExtrusion],
            },
        };
        if (map.getSource('temp-geo-record-extrusion') !== undefined) {
            map.removeLayer('polygon-extrusion-temp');
            map.removeSource('temp-geo-record-extrusion');
        }

        if (!map.getSource('temp-geo-record-extrusion')) {
            map.addSource(
                'temp-geo-record-extrusion',
                geoRecordExtrusionSource,
            );
        }
        if (!map.getLayer('polygon-extrusion-temp')) {
            map.addLayer(
                {
                    id: 'polygon-extrusion-temp',
                    type: 'fill-extrusion',
                    source: 'temp-geo-record-extrusion',
                    paint: {
                        'fill-extrusion-color': ['get', 'color'],
                        'fill-extrusion-height': ['get', 'height'],
                        'fill-extrusion-opacity': 0.8,
                    },
                },
                'geo-record-title',
            );
        }
    };

    const removeHighlightGeoRecordExtrusion = (
        map: mapboxgl.Map,
        geoRecordExtrusion: any,
    ) => {
        if (map.getSource('temp-geo-record-extrusion')) {
            geoRecordExtrusion.properties.isVisible = true;
            map.removeLayer('polygon-extrusion-temp');
            map.removeSource('temp-geo-record-extrusion');
        }
    };

    const startTranslate = (draw: mapboxgl.Draw) => {
        draw.changeMode('DrawTranslateMode', {
            featureIds: ['test'],
        });
    };

    const startRotate = (draw: mapboxgl.Draw) => {
        draw.changeMode('drawRotateMode', {
            selectedFeatures: ['test'],
        });
    };

    const startEdit = (draw: mapboxgl.Draw) => {
        draw.changeMode('DrawEditMode', {
            featureId: 'test',
        });
    };

    const startDrawRectangle = (draw: mapboxgl.Draw) => {
        draw.add({
            id: 'temp-rectangle',
            type: 'Feature',
            properties: {},
            geometry: {
                type: 'Polygon',
                coordinates: [],
            },
        });
        draw.changeMode('DrawRectangleMode', {
            selectedFeature: { id: 'temp-rectangle' },
        });
    };

    const initGeoRecordLayers = (map: mapboxgl.Map) => {
        geoRecordLayers.forEach((geoRecordLayer) => {
            if (!map.getSource(geoRecordLayer.source)) return;
            map.addLayer(geoRecordLayer);
        });
    };

    const removeGeoRecordLayers = (map: mapboxgl.Map) => {
        if (map.getLayer('map-icons')) {
            map.removeLayer('map-icons');
        }
        geoRecordLayers.forEach((geoRecordLayer) => {
            if (map.getLayer(geoRecordLayer.id)) {
                map.removeLayer(geoRecordLayer.id);
            }
        });
    };

    const treeToArray = (tree: any): any[] => {
        let array: any[] = [];

        tree.forEach((node: any) => {
            if (node.type === 'geo_record') {
                array.push(node.geoRecordGeoJson);
            } else {
                array.push(...treeToArray(node.children));
            }
        });

        return array;
    };

    const categoriesToArray = (cats: any): any[] => {
        let array: any[] = [];

        cats.forEach((cat: any) => {
            array.push(cat);
            if (cat.children && cat.children.length !== 0) {
                array.push(...categoriesToArray(cat.children));
            }
        });

        return array;
    };

    /**
     * Initialize a mapbox source for duplicating a geo record.
     */
    const initDuplicateSource = (map: mapboxgl.Map, geoRecord: any) => {
        const tempSource = {
            type: 'geojson',
            data: Array.isArray(geoRecord)
                ? {
                      type: 'FeatureCollection',
                      features: geoRecord.map((x) => generateMapboxFeature(x)),
                  }
                : generateMapboxFeature(geoRecord),
        };
        map.addSource('temp-geo-records', tempSource);
        if (geoRecord.height > 0) {
            map.addLayer({
                id: 'temp-geo-record-polygon-extrusion',
                type: 'fill-extrusion',
                source: 'temp-geo-records',
                paint: {
                    'fill-extrusion-color': '#ffff00',
                    'fill-extrusion-height': ['get', 'height'],
                    'fill-extrusion-opacity': 0.2,
                },
                filter: ['all', ['==', ['geometry-type'], 'Polygon']],
            });
        }
        map.addLayer({
            id: 'temp-geo-record-polygon',
            type: 'fill',
            source: 'temp-geo-records',
            paint: {
                'fill-color': '#ffff00',
                'fill-opacity': 0.2,
            },
            filter: ['all', ['==', ['geometry-type'], 'Polygon']],
        });

        map.addLayer({
            id: 'temp-geo-record-polygon-outline',
            type: 'line',
            source: 'temp-geo-records',
            layout: {
                'line-cap': 'round',
                'line-join': 'round',
            },
            paint: {
                'line-width': 1,
                'line-color': '#ffff00',
                'line-dasharray': [2, 2],
            },
            filter: ['==', ['geometry-type'], 'Polygon'],
        });

        map.addLayer({
            id: 'temp-geo-record-linestring',
            type: 'line',
            source: 'temp-geo-records',
            layout: {
                'line-cap': 'round',
                'line-join': 'round',
            },
            paint: {
                'line-width': 1,
                'line-color': '#ffff00',
                'line-dasharray': [2, 2],
            },
            filter: ['==', ['geometry-type'], 'LineString'],
        });

        map.addLayer({
            id: 'temp-geo-record-points',
            type: 'circle',
            source: 'temp-geo-records',
            paint: {
                'circle-radius': 4,
                'circle-color': '#ffff00',
                'circle-stroke-color': '#ffffff',
                'circle-stroke-width': 1,
            },
            filter: ['==', ['geometry-type'], 'Point'],
        });
    };

    /**
     * Remove temporary duplicate source and layers.
     */
    const removeDuplicateSource = (map: mapboxgl.Map) => {
        if (map.getSource('temp-geo-records')) {
            if (map.getLayer('temp-geo-record-polygon-extrusion')) {
                map.removeLayer('temp-geo-record-polygon-extrusion');
            }
            map.removeLayer('temp-geo-record-points');
            map.removeLayer('temp-geo-record-linestring');
            map.removeLayer('temp-geo-record-polygon');
            map.removeLayer('temp-geo-record-polygon-outline');
            map.removeSource('temp-geo-records');
        }
    };

    /**
     * Initialize a mapbox source for measuring a geo record.
     */
    const initMeasureAreaSource = (map: mapboxgl.Map, geoRecord: any) => {
        const tempSource = {
            type: 'geojson',
            data: generateMapboxFeature(geoRecord),
        };
        map.addSource('temp-geo-records', tempSource);

        map.addLayer({
            id: 'temp-geo-record-polygon',
            type: 'fill',
            source: 'temp-geo-records',
            paint: {
                'fill-color': '#ffffff',
                'fill-opacity': 0.2,
            },
            filter: ['all', ['==', ['geometry-type'], 'Polygon']],
        });
    };

    /**
     * Remove temporary measure source and layers.
     */
    const removeMeasureAreaSource = (map: mapboxgl.Map) => {
        map.removeLayer('temp-geo-record-polygon');
        if (map.getSource('temp-geo-records')) {
            map.removeSource('temp-geo-records');
        }
    };

    /**
     * Initialize a mapbox source for rectangle tool to create geo record.
     */
    const initRectangleGeoRecordSource = (
        map: mapboxgl.Map,
        geoRecord: any,
    ) => {
        const tempSource = {
            type: 'geojson',
            data: generateMapboxFeature(geoRecord),
        };
        map.addSource('rectangle-geo-records', tempSource);

        map.addLayer({
            id: 'rectangle-geo-record-polygon',
            type: 'fill',
            source: 'rectangle-geo-records',
            paint: {
                'fill-color': '#232c39',
                'fill-opacity': 0.6,
            },
            filter: ['all', ['==', ['geometry-type'], 'Polygon']],
        });
    };

    /**
     * Remove temporary rectangle source and layers.
     */
    const removeRectangleGeoRecordSource = (map: mapboxgl.Map) => {
        map.removeLayer('rectangle-geo-record-polygon');
        if (map.getSource('rectangle-geo-records')) {
            map.removeSource('rectangle-geo-records');
        }
    };

    /**
     * Initialize a mapbox source for create tool to create geo record.
     */
    const initCreateGeoRecordSource = (map: mapboxgl.Map, geoRecord: any) => {
        const tempSource = {
            type: 'geojson',
            data: {
                id: 'create',
                type: 'Feature',
                geometry: geoRecord.geometry,
            },
        };

        if (!map.getSource('creating-geo-record')) {
            map.addSource('creating-geo-record', tempSource);
        }

        modificationLayers.forEach((layer) => {
            if (map.getLayer(layer)) {
                map.addLayer(layer);
            }
        });
    };

    /**
     * Remove temporary create source and layers.
     */
    const removeCreateGeoRecordSource = (map: mapboxgl.Map) => {
        if (map.getSource('creating-geo-record')) {
            if (map.getLayer('create-geo-record-polygon')) {
                map.removeLayer('create-geo-record-polygon');
            }
            if (map.getLayer('create-geo-record-linestring')) {
                map.removeLayer('create-geo-record-linestring');
            }
            if (map.getLayer('create-geo-record-points')) {
                map.removeLayer('create-geo-record-points');
            }
            if (map.getLayer('create-geo-record-extrusion')) {
                map.removeLayer('create-geo-record-extrusion');
            }
            map.removeSource('creating-geo-record');
        }
    };

    const initMultiGeoRecordSource = (map: mapboxgl, geoRecords: any) => {
        const multipleGeoRecordSelect = {
            type: 'geojson',
            maxzoom: 16,
            buffer: 512,
            data: {
                type: 'FeatureCollection',
                features: geoRecords.map((x) => {
                    return generateMapboxFeature(x);
                }),
            },
        };
        removeCreateGeoRecordSource(map);

        map.addSource('creating-geo-record', multipleGeoRecordSelect);
        modificationLayers.forEach((layer) => {
            map.addLayer(layer);
        });
    };

    /**
     * Initialize a mapbox source for select tool to highlight geo record selected.
     */
    const initSelectedGeoRecord = (map: mapboxgl.Map, geoRecord: any) => {
        const source = map.getSource('geo-records');
        if(!(source && source._data && source._data.features)){
            return
        }
        const features = source._data.features;

        features.find(
            (target: any) => target.id == geoRecord.id,
        ).properties.isVisible = false;

        source.setData({
            type: 'FeatureCollection',
            features: features,
        });

        const tempSource = {
            type: 'geojson',
            data: {
                id: 'create',
                type: 'Feature',
                geometry: features.find(
                    (target: any) => target.id == geoRecord.id,
                ).geometry,
            },
        };
        map.addSource('selected-geo-records', tempSource);

        map.addLayer({
            id: 'selected-geo-record-polygon',
            type: 'fill',
            source: 'selected-geo-records',
            paint: {
                'fill-color': '#D20C0C',
                'fill-outline-color': '#D20C0C',
                'fill-opacity': 0.3,
            },
            filter: ['all', ['==', ['geometry-type'], 'Polygon']],
        });

        map.addLayer({
            id: 'selected-geo-record-polygon-outline',
            type: 'line',
            source: 'selected-geo-records',
            layout: {
                'line-cap': 'round',
                'line-join': 'round',
            },
            paint: {
                'line-color': '#D20C0C',
                'line-dasharray': [0.2, 2],
                'line-width': 2,
            },
            filter: ['==', ['geometry-type'], 'Polygon'],
        });

        map.addLayer({
            id: 'selected-geo-record-linestring',
            type: 'line',
            source: 'selected-geo-records',
            layout: {
                'line-cap': 'round',
                'line-join': 'round',
            },
            paint: {
                'line-width': 2,
                'line-color': '#D20C0C',
            },
            filter: ['==', ['geometry-type'], 'LineString'],
        });

        map.addLayer({
            id: 'selected-geo-record-points',
            type: 'circle',
            source: 'selected-geo-records',
            paint: {
                'circle-radius': 6,
                'circle-color': '#D20C0C',
            },
            filter: ['==', ['geometry-type'], 'Point'],
        });
    };

    /**
     * Remove temporary selected source and layers.
     */
    const removeSelectedGeoRecord = (map: mapboxgl.Map, geoRecord: any) => {
        const source = map.getSource('geo-records');
        const features = source._data.features;

        if (map.getSource('selected-geo-records') && geoRecord.id) {
            features.find(
                (target: any) => target.id == geoRecord.id,
            ).properties.isVisible = true;

            source.setData({
                type: 'FeatureCollection',
                features: features,
            });
            map.removeLayer('selected-geo-record-polygon');
            map.removeLayer('selected-geo-record-polygon-outline');
            map.removeLayer('selected-geo-record-linestring');
            map.removeLayer('selected-geo-record-points');
            map.removeSource('selected-geo-records');
        }
    };

    /**
     * Initialize a mapbox source for cursor.
     */
    const initCursorSource = (map: mapboxgl.Map) => {
        const tempSource = {
            type: 'geojson',
            data: {
                id: 'create-cursor',
                type: 'Feature',
                geometry: { type: 'Point', coordinates: [0, 0] },
            },
        };
        map.addSource('create-cursor-point', tempSource);
        map.addLayer({
            id: 'create-cursor-point-outer-layer',
            type: 'circle',
            source: 'create-cursor-point',
            paint: {
                'circle-radius': 5,
                'circle-color': '#ffffff',
            },
            filter: ['==', ['geometry-type'], 'Point'],
        });

        map.addLayer({
            id: 'create-cursor-point-layer',
            type: 'circle',
            source: 'create-cursor-point',
            paint: {
                'circle-radius': 3,
                'circle-color': '#D20C0C',
            },
            filter: ['==', ['geometry-type'], 'Point'],
        });
    };

    const setArchiveGeoRecordVisibility = (
        map: mapboxgl.Map,
        archiveEventId: number,
    ) => {
        const source = map.getSource('geo-records-archive');
        const features = source._data.features;
        features.forEach((feature) => {
            if (archiveEventId === feature.properties.archiveEventId) {
                feature.properties.isVisible = true;
            } else {
                feature.properties.isVisible = false;
            }
        });
        source.setData({
            type: 'FeatureCollection',
            features: features,
        });
    };

    const setArchiveGeoRecordVisibilityByCategory = (
        map: mapboxgl.Map,
        selectedGeoRecordArchiveNodes: any,
    ): void => {
        let selectedKeys = Object.keys(selectedGeoRecordArchiveNodes).map(
            (key) => parseInt(key),
        );

        const source = map.getSource('geo-records-archive');
        const extrusionSource = map.getSource('geo-records-archive-extrusion');
        const extrusionFeatures = extrusionSource._data.features;
        const features = source._data.features;

        features.forEach((feature: any) => {
            feature.properties.isVisible = selectedKeys.includes(
                feature.properties.geoRecordCategoryId,
            );
        });

        extrusionFeatures.forEach((extrusionFeature: any) => {
            extrusionFeature.properties.isVisible = selectedKeys.includes(
                extrusionFeature.properties.geoRecordCategoryId,
            );
        });

        source.setData({
            type: 'FeatureCollection',
            features: features,
        });

        extrusionSource.setData({
            type: 'FeatureCollection',
            features: extrusionFeatures,
        });
    };

    /**
     * Remove temporary cursor source and layers.
     */
    const removeCursorSource = (map: mapboxgl.Map) => {
        if (map.getLayer('create-cursor-point-layer')) {
            map.removeLayer('create-cursor-point-layer');
            map.removeLayer('create-cursor-point-outer-layer');
        }
        if (map.getSource('create-cursor-point')) {
            map.removeSource('create-cursor-point');
        }
    };

    return {
        initCursorSource,
        removeCursorSource,
        initGeoRecordSource,
        initGeoRecordLayers,
        removeGeoRecordLayers,
        treeToArray,
        categoriesToArray,
        refreshGeoRecord,
        addGeoRecord,
        modifyGeometry,
        initDuplicateSource,
        removeDuplicateSource,
        removeMeasureAreaSource,
        initMeasureAreaSource,
        toggleGeoRecord,
        startTranslate,
        startRotate,
        startEdit,
        initRectangleGeoRecordSource,
        removeRectangleGeoRecordSource,
        initCreateGeoRecordSource,
        removeCreateGeoRecordSource,
        removeGeoRecord,
        initSelectedGeoRecord,
        removeSelectedGeoRecord,
        initGeoRecordExtrusion,
        removeGeoRecordExtrusionLayers,
        addGeoRecordExtrusion,
        updateGeoRecordExtrusion,
        removeGeoRecordExtrusion,
        refreshGeoRecordExtrusion,
        createGeoRecordExtrusion,
        refreshCreatedGeoRecordExtrusion,
        removeCreatedGeoRecordExtrusion,
        addHighlightGeoRecordExtrusion,
        removeHighlightGeoRecordExtrusion,
        initMultiGeoRecordSource,
        startDrawRectangle,
        updateVertexLabel,
        removeVertexLabel,
        generateMapboxFeature,
        initArchiveGeoRecordSource,
        setArchiveGeoRecordVisibility,
        setArchiveGeoRecordVisibilityByCategory,
        initGeoRecordArchiveExtrusion,
        removeGeoRecordArchiveExtrusionLayers,
    };
};
